/**
 * Analytics Service for Umami Analytics Integration
 *
 * This service provides a centralized interface for tracking user behavior,
 * admin actions, and content engagement across the Northern Championship Series platform.
 *
 * Features:
 * - Automatic Umami script loading
 * - Environment-aware tracking (disabled in development)
 * - Type-safe event tracking
 * - Console logging for development debugging
 */

class AnalyticsService {
  private isInitialized: boolean = false;
  private isEnabled: boolean = false;
  private websiteId: string | undefined;

  constructor() {
    this.initializeUmami();
  }

  /**
   * Initialize Umami analytics by loading the tracking script
   */
  private initializeUmami(): void {
    // Get environment variables
    const websiteId = process.env.REACT_APP_UMAMI_WEBSITE_ID;
    const scriptSrc = process.env.REACT_APP_UMAMI_SRC;

    // Only enable in production and if environment variables are set
    this.isEnabled =
      process.env.NODE_ENV === 'production' &&
      !!websiteId &&
      !!scriptSrc;

    if (!this.isEnabled) {
      console.log('[Analytics] Umami disabled (development mode or missing config)');
      return;
    }

    this.websiteId = websiteId;

    // Check if script is already loaded
    if (document.querySelector(`script[data-website-id="${websiteId}"]`)) {
      this.isInitialized = true;
      console.log('[Analytics] Umami already loaded');
      return;
    }

    // Create and inject Umami script tag
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = scriptSrc!;
    script.setAttribute('data-website-id', websiteId!);

    // Optional: Customize script to avoid ad blockers
    // script.setAttribute('data-auto-track', 'true');
    // script.setAttribute('data-cache', 'true');

    script.onload = () => {
      this.isInitialized = true;
      console.log('[Analytics] Umami loaded successfully');
    };

    script.onerror = () => {
      console.error('[Analytics] Failed to load Umami script');
    };

    document.head.appendChild(script);
  }

  /**
   * Track a custom event
   * @param eventName - Name of the event (e.g., 'admin_article_created')
   * @param eventData - Optional data object with event properties
   */
  trackEvent(eventName: string, eventData?: Record<string, any>): void {
    if (this.isEnabled && window.umami) {
      window.umami.track(eventName, eventData);
    } else {
      // Log in development for debugging
      console.log('[Analytics Event]', eventName, eventData);
    }
  }

  /**
   * Identify the current user session with a distinct ID
   * Links the session to a user for cross-session tracking
   * @param userId - Unique identifier for the user (Steam ID recommended)
   * @param userData - Optional user metadata (role, username, etc.)
   */
  identifyUser(userId: string, userData?: Record<string, any>): void {
    if (this.isEnabled && window.umami) {
      window.umami.identify(userId, userData);
      console.log('[Analytics] User identified:', userId);
    } else {
      // Log in development for debugging
      console.log('[Analytics Identify]', userId, userData);
    }
  }

  /**
   * Admin-specific event tracking methods
   */
  admin = {
    /**
     * Track article creation
     */
    articleCreated: (category: string, hasImage: boolean = false) => {
      this.trackEvent('admin_article_created', { category, hasImage });
    },

    /**
     * Track article editing
     */
    articleEdited: (articleId: string, category: string) => {
      this.trackEvent('admin_article_edited', { articleId, category });
    },

    /**
     * Track article deletion
     */
    articleDeleted: (articleId: string) => {
      this.trackEvent('admin_article_deleted', { articleId });
    },

    /**
     * Track article publishing (if separate from creation)
     */
    articlePublished: (articleId: string, category: string) => {
      this.trackEvent('admin_article_published', { articleId, category });
    },

    /**
     * Track user creation
     */
    userCreated: (role: string) => {
      this.trackEvent('admin_user_created', { role });
    },

    /**
     * Track user editing
     */
    userEdited: (userId: string, changedFields: string[]) => {
      this.trackEvent('admin_user_edited', {
        userId,
        changedFields: changedFields.join(','),
        fieldCount: changedFields.length
      });
    },

    /**
     * Track user deletion
     */
    userDeleted: (userId: string) => {
      this.trackEvent('admin_user_deleted', { userId });
    },

    /**
     * Track permission changes
     */
    permissionsChanged: (userId: string, oldRole: string, newRole: string) => {
      this.trackEvent('admin_permissions_changed', {
        userId,
        oldRole,
        newRole
      });
    },

    /**
     * Track team creation
     */
    teamCreated: (teamName: string, season: number) => {
      this.trackEvent('admin_team_created', { teamName, season });
    },

    /**
     * Track team editing
     */
    teamEdited: (teamId: string, teamName: string) => {
      this.trackEvent('admin_team_edited', { teamId, teamName });
    },

    /**
     * Track roster updates
     */
    rosterUpdated: (teamId: string, playerCount: number) => {
      this.trackEvent('admin_roster_updated', { teamId, playerCount });
    },

    /**
     * Track match creation
     */
    matchCreated: (division: string, isPlayoff: boolean = false) => {
      this.trackEvent('admin_match_created', { division, isPlayoff });
    },

    /**
     * Track match editing
     */
    matchEdited: (matchId: string) => {
      this.trackEvent('admin_match_edited', { matchId });
    },

    /**
     * Track veto completion
     */
    vetoCompleted: (matchId: string, mapCount: number) => {
      this.trackEvent('admin_veto_completed', { matchId, mapCount });
    },

    /**
     * Track server configuration
     */
    serverConfigured: (serverId: string) => {
      this.trackEvent('admin_server_configured', { serverId });
    },

    /**
     * Track tournament creation/updates
     */
    tournamentUpdated: (tournamentId: string, action: 'created' | 'updated' | 'deleted') => {
      this.trackEvent('admin_tournament_updated', { tournamentId, action });
    },
  };

  /**
   * Content engagement tracking methods
   */
  content = {
    /**
     * Track article views
     */
    articleViewed: (articleId: string, category: string, title: string) => {
      this.trackEvent('article_viewed', { articleId, category, title });
    },

    /**
     * Track news category filtering
     */
    categoryFiltered: (category: string) => {
      this.trackEvent('news_category_filtered', { category });
    },

    /**
     * Track social media shares (if implemented)
     */
    articleShared: (articleId: string, platform: string) => {
      this.trackEvent('news_shared', { articleId, platform });
    },

    /**
     * Track match page views
     */
    matchViewed: (matchId: string, division: string, status: 'upcoming' | 'live' | 'played') => {
      this.trackEvent('match_viewed', { matchId, division, status });
    },

    /**
     * Track match stats viewing
     */
    matchStatsViewed: (matchId: string) => {
      this.trackEvent('match_stats_viewed', { matchId });
    },

    /**
     * Track live match watching
     */
    liveMatchWatched: (matchId: string, duration?: number) => {
      this.trackEvent('live_match_watched', { matchId, duration });
    },

    /**
     * Track veto viewing
     */
    vetoViewed: (matchId: string) => {
      this.trackEvent('veto_viewed', { matchId });
    },

    /**
     * Track team profile views
     */
    teamViewed: (teamId: string, teamName: string, season: number) => {
      this.trackEvent('team_viewed', { teamId, teamName, season });
    },

    /**
     * Track player profile views
     */
    playerProfileViewed: (steamId: string, playerName?: string) => {
      this.trackEvent('player_profile_viewed', { steamId, playerName });
    },

    /**
     * Track leaderboard views
     */
    leaderboardViewed: (season: number, division?: string) => {
      this.trackEvent('leaderboard_viewed', { season, division });
    },
  };

  /**
   * User interaction tracking methods
   */
  interaction = {
    /**
     * Track search usage
     */
    searchPerformed: (query: string, resultCount: number) => {
      this.trackEvent('search_performed', {
        query,
        resultCount,
        hasResults: resultCount > 0
      });
    },

    /**
     * Track filter application
     */
    filterApplied: (filterType: string, value: string) => {
      this.trackEvent('filter_applied', { filterType, value });
    },

    /**
     * Track season selection
     */
    seasonChanged: (oldSeason: number, newSeason: number) => {
      this.trackEvent('season_changed', { oldSeason, newSeason });
    },

    /**
     * Track profile editing
     */
    profileEdited: (userId: string, changedFields: string[]) => {
      this.trackEvent('profile_edited', {
        userId,
        changedFields: changedFields.join(',')
      });
    },

    /**
     * Track profile picture uploads
     */
    profilePictureUploaded: (userId: string) => {
      this.trackEvent('profile_picture_uploaded', { userId });
    },

    /**
     * Track Steam login completions
     */
    steamLoginCompleted: (userId: string) => {
      this.trackEvent('steam_login_completed', { userId });
    },

    /**
     * Track navigation to important pages
     */
    pageVisited: (pageName: string, metadata?: Record<string, any>) => {
      this.trackEvent('page_visited', { pageName, ...metadata });
    },
  };

  /**
   * Check if analytics is enabled and initialized
   */
  isReady(): boolean {
    return this.isEnabled && this.isInitialized;
  }

  /**
   * Get current configuration status (for debugging)
   */
  getStatus(): { enabled: boolean; initialized: boolean; websiteId?: string } {
    return {
      enabled: this.isEnabled,
      initialized: this.isInitialized,
      websiteId: this.websiteId,
    };
  }
}

// Export a singleton instance
export const analytics = new AnalyticsService();
